import { latLonToEN, pointsToEN } from '../ol-utils';
import { FeatureMouseInteraction } from '@openlayers/interaction/FeatureMouseInteraction';
import Feature, { FeatureLike } from 'ol/Feature';

import { LineString, Point as OlPoint } from 'ol/geom';
import * as olStyle from 'ol/style';
import { DxfService } from '@openlayers/DxfService';
import { ColorService } from '@shared/codelist/color-service';

const FEATURE_TYPE = 'pipe';

const sharedStyles = {
    hitArea: new olStyle.Style({
        stroke: new olStyle.Stroke({
            color: [0, 0, 0, 0.005],
            width: 15,
        }),
    }),
};

const stylesCache = {};

function styleFunction(feature: FeatureLike) {
    const final = [sharedStyles.hitArea];
    const data: IPipeNetwork = feature.get('data');

    const isRect = data.props?.type === 'RectStruct';
    const styles = createStyleForData(data);

    if (feature.get('hover')) {
        final.push(styles.lineHighlight, isRect ? styles.squareHighlight : styles.circleHighlight);
    } else {
        final.push(styles.line, isRect ? styles.square : styles.circle);
    }
    return final;
}

function createStyleForData(data: IPipeNetwork) {
    // SHP+QML: Pass linetype and customdash from props
    const lineType = (data.props as any)?.linetype;
    const customDash = (data.props as any)?.customdash;
    return createStyle(DxfService.getColorFromEntity(data), lineType, customDash);
}

function createStyle(
    rgbStr: string,
    lineType?: string,
    customDash?: string,
    lineWidth?: number
): {
    line: olStyle.Style;
    lineHighlight: olStyle.Style;
    circle: olStyle.Style;
    circleHighlight: olStyle.Style;
    square: olStyle.Style;
    squareHighlight: olStyle.Style;
} {
    rgbStr = rgbStr || '0,0,0';
    const rgb: any[] = rgbStr.split(',');
    if (rgb.length != 3) rgbStr = '0,0,0';

    const cacheKey = `${rgbStr}_${lineType || ''}_${customDash || ''}_${lineWidth || ''}`;
    if (stylesCache[cacheKey]) return stylesCache[cacheKey];

    // SHP+QML: Apply dash pattern - use customdash if present, otherwise check linetype
    let lineDash: number[] | undefined = undefined;
    if (customDash) {
        lineDash = customDash.split(';').map(v => parseFloat(v) * 3); // Scale for visibility
    } else if (lineType === 'dash') {
        lineDash = [10, 5];
    } else if (lineType === 'dot') {
        lineDash = [1, 3];
    } else if (lineType === 'dashdot') {
        lineDash = [10, 5, 1, 5];
    }

    const width = lineWidth || 1.2;

    const styles = {
        line: new olStyle.Style({
            stroke: new olStyle.Stroke({
                color: [rgb[0], rgb[1], rgb[2], 0.8],
                width: width,
                lineDash: lineDash, // SHP+QML: dash pattern
            }),
        }),

        circle: new olStyle.Style({
            image: new olStyle.Circle({
                radius: 5,
                fill: new olStyle.Fill({
                    color: 'rgba(0,0,0,0.01)',
                }),
                stroke: new olStyle.Stroke({
                    color: [rgb[0], rgb[1], rgb[2], 0.8],
                    width: 1.2,
                }),
            }),
        }),

        square: new olStyle.Style({
            image: new olStyle.RegularShape({
                radius: 6,
                fill: new olStyle.Fill({
                    color: 'rgba(0,0,0,0.01)',
                }),
                stroke: new olStyle.Stroke({
                    color: [rgb[0], rgb[1], rgb[2], 0.8],
                    width: 1.2,
                }),
                points: 4,
                angle: Math.PI / 4,
            }),
        }),

        lineHighlight: new olStyle.Style({
            stroke: new olStyle.Stroke({
                color: [rgb[0], rgb[1], rgb[2], 0.8],
                width: width * 2, // Highlight is 2x wider
                lineDash: lineDash, // SHP+QML: dash pattern
            }),
        }),

        circleHighlight: new olStyle.Style({
            image: new olStyle.Circle({
                radius: 5,
                fill: new olStyle.Fill({
                    color: 'rgba(0,0,0,0.01)',
                }),
                stroke: new olStyle.Stroke({
                    color: [rgb[0], rgb[1], rgb[2], 0.8],
                    width: 3,
                }),
            }),
        }),

        squareHighlight: new olStyle.Style({
            image: new olStyle.RegularShape({
                radius: 6,
                fill: new olStyle.Fill({
                    color: 'rgba(0,0,0,0.01)',
                }),
                stroke: new olStyle.Stroke({
                    color: [rgb[0], rgb[1], rgb[2], 0.8],
                    width: 3,
                }),
                points: 4,
                angle: Math.PI / 4,
            }),
        }),
    };

    stylesCache[cacheKey] = styles;
    return styles;
}

function makeFeatures(network: IPipeNetwork, mouseInteraction?: FeatureMouseInteraction): Feature[] {
    const features: Feature[] = [];

    const source: FeatureSource = {
        modelId: network.modelId,
    };

    for (const layer of network.layers) {
        // each layer has array of polylines and points

        for (const polyline of layer.polylines) {
            const coords: Coordinate[] = pointsToEN(polyline.points);
            if (!coords) {
                console.warn('Skipping bad pipe network: ', network, polyline);
                return null;
            }

            const feature = new Feature({
                geometry: new LineString(coords),
            });

            const color = ColorService.getPipeColorFromStringLine(polyline);
            const rgb = [color.r, color.g, color.b];
            polyline.color = rgb.join(',');

            feature.set('source', source);
            feature.set('data', polyline);
            feature.set('props', polyline.props);
            feature.set('type', FEATURE_TYPE);
            feature.set('mouseInteraction', mouseInteraction);
            features.push(feature);
        }

        for (const point of layer.points) {
            const coord = latLonToEN(point.lat, point.lon);

            if (coord) {
                let t = '';
                let d = '';
                let w = '';
                let l = '';

                if (typeof point.props !== 'undefined' && point.props !== null) {
                    if (typeof point.props.type !== 'undefined' && point.props.type !== null) {
                        t = point.props.type;
                    }
                    if (typeof point.props.diameter !== 'undefined' && point.props.diameter !== null) {
                        d = point.props.diameter;
                    }
                    if (typeof point.props.width !== 'undefined' && point.props.width !== null) {
                        w = point.props.width;
                    }
                    if (typeof point.props.length !== 'undefined' && point.props.length !== null) {
                        l = point.props.length;
                    }
                }

                if ((t === 'CircStruct' && Number(d) !== 0) || (t === 'RectStruct' && Number(l) !== 0 && Number(w) !== 0)) {
                    const feature = new Feature({
                        geometry: new OlPoint(coord),
                    });

                    const color = ColorService.getPipeColorFromPipePoint(point);
                    const rgb = [color.r, color.g, color.b];
                    point.color = rgb.join(',');

                    feature.set('source', source);
                    feature.set('data', point);
                    feature.set('props', point.props);
                    feature.set('type', FEATURE_TYPE);
                    feature.set('mouseInteraction', mouseInteraction);
                    features.push(feature);
                }
            }
        }
    }

    return features;
}

function makeArrowFeatures(sourceFeature: Feature): Feature[] {
    const features = [];
    const data = sourceFeature.get('data');
    const geometry = sourceFeature.getGeometry() as LineString;

    if (!geometry.forEachSegment || !data.points?.length || data.points.length < 2) {
        return features;
    }

    let segmentIndex = 0;
    geometry.forEachSegment((start, end) => {
        const segmentPoints = getArrowSegmentPoints(data, segmentIndex);
        segmentIndex++;

        if (!segmentPoints) {
            return;
        }

        if (segmentPoints.startPoint.elevation < segmentPoints.endPoint.elevation) {
            [start, end] = [end, start];
        }
        const dx = end[0] - start[0];
        const dy = end[1] - start[1];
        const rotation = Math.atan2(dy, dx);

        const arrowFeature = new Feature({
            geometry: new OlPoint(end),
        });
        arrowFeature.set('rotation', -rotation + 1.57);
        arrowFeature.set('offset', [0, -15]);
        arrowFeature.set('source', sourceFeature.get('source'));
        features.push(arrowFeature);
    });
    return features;
}

const makeArrowLineCoordinates = (feature: Feature, size = 7): number[][][][] => {
    const result: number[][][][] = [];
    const data = feature.get('data');
    const geometry = feature.getGeometry() as LineString;
    const arrowSize = size;
    const arrowAngle = Math.PI / 6;

    if (!geometry.forEachSegment || !data.points?.length || data.points.length < 2) {
        return result;
    }

    let segmentIndex = 0;
    geometry.forEachSegment((start, end) => {
        const segmentPoints = getArrowSegmentPoints(data, segmentIndex);
        segmentIndex++;

        if (!segmentPoints) {
            return;
        }

        if (segmentPoints.startPoint.elevation < segmentPoints.endPoint.elevation) {
            [start, end] = [end, start];
        }

        const dx = end[0] - start[0];
        const dy = end[1] - start[1];
        const angle = Math.atan2(dy, dx);

        const leftAngle = angle + Math.PI - arrowAngle;
        const rightAngle = angle + Math.PI + arrowAngle;

        const leftTip = [end[0] + arrowSize * Math.cos(leftAngle), end[1] + arrowSize * Math.sin(leftAngle)];
        const rightTip = [end[0] + arrowSize * Math.cos(rightAngle), end[1] + arrowSize * Math.sin(rightAngle)];

        result.push([[end, leftTip, rightTip, end]]);
    });

    return result;
};

function getArrowSegmentPoints(data: IPipeNetwork, segmentIndex: number) {
    const startPoint = data.points?.[segmentIndex];
    const endPoint = data.points?.[segmentIndex + 1];

    if (
        !startPoint ||
        !endPoint ||
        startPoint.elevation == null ||
        endPoint.elevation == null ||
        startPoint.elevation === endPoint.elevation ||
        (startPoint.lat === endPoint.lat && startPoint.lon === endPoint.lon)
    ) {
        return null;
    }

    return {
        startPoint,
        endPoint,
    };
}

export const PipeFeatureFactory = {
    makeFeatures,
    makeArrowFeatures,
    makeArrowLineCoordinates,
    styleFunction,
    FEATURE_TYPE,
};
